import { StandardEvents } from '@shopify/events';
import { formatMoney as formatMoneyWithFormat } from '@theme/money-formatting';

/**
 * Add-to-cart confirmation modal — 1:1 port of the legacy WP "xoo-cp"
 * (Added To Cart Popup) plugin behavior. Opens automatically whenever a
 * `CartLinesUpdateEvent` with action "add" resolves successfully anywhere
 * on the page (native PDP form, related-products quick-add, etc.), and
 * shows the specific line that was added plus the running cart total.
 */
class PdpCartModal extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-cart-modal-overlay]');
    this.dialog = this.querySelector('.amd-cart-modal__dialog');
    this.content = this.querySelector('[data-cart-modal-content]');
    this.template = document.querySelector('[data-cart-modal-item-template]');

    this.querySelectorAll('[data-cart-modal-close]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    });

    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-active')) {
        this.close();
      }
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
  }

  handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    // merchandiseId arrives as a GID ("gid://shopify/ProductVariant/123") —
    // the CartLinesUpdateEvent constructor normalizes raw ids — while
    // /cart.js items carry numeric variant_id, so compare the trailing id.
    const merchandiseId = event.lines?.[0]?.merchandiseId;
    const variantId = String(merchandiseId ?? '').split('/').pop() || '';

    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;

        const items = detail?.items || [];
        const addedItem = items.find((item) => String(item.variant_id) === variantId) || items[0];

        if (!addedItem) return;

        const compareAtPrice = typeof detail?.compareAtPrice === 'number' ? detail.compareAtPrice : this.getCompareAtPrice(variantId);
        this.render(addedItem, cart?.totalQuantity, compareAtPrice);
        this.open();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pdp-cart-modal] Event promise rejected:', error);
      });
  };

  /**
   * The cart AJAX API (/cart/add.js) doesn't return compare_at_price on line
   * items. The custom PDP form (product-pdp.js) already sends it in the
   * event's `detail`; for other add-to-cart entry points (quick-add, related
   * products) that go through Horizon's standard add-to-cart-component, read
   * it from the matching element's data attribute instead — rendered per
   * variant by add-to-cart-button.liquid.
   * @param {string} variantId
   * @returns {number|null}
   */
  getCompareAtPrice(variantId) {
    if (!variantId) return null;

    const source = document.querySelector(`add-to-cart-component[data-variant-id="${variantId}"]`);
    const compareAtPrice = Number(source?.dataset.compareAtPrice);

    return compareAtPrice > 0 ? compareAtPrice : null;
  }

  render(item, totalQuantity, compareAtPrice) {
    if (!this.template || !this.content) return;

    const fragment = this.template.content.cloneNode(true);

    const successText = fragment.querySelector('[data-cart-modal-success-text]');
    if (successText) successText.textContent = this.dataset.successText || 'Product successfully added to your cart';

    const link = fragment.querySelector('[data-cart-modal-product-link]');
    const title = fragment.querySelector('[data-cart-modal-product-title]');
    if (link) link.href = item.url || '#';
    if (title) title.textContent = item.product_title || item.title || '';

    const variantEl = fragment.querySelector('[data-cart-modal-product-variant]');
    if (variantEl) {
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantEl.textContent = item.variant_title;
        variantEl.hidden = false;
      } else {
        variantEl.hidden = true;
      }
    }

    const priceEl = fragment.querySelector('[data-cart-modal-product-price]');
    if (priceEl) priceEl.textContent = this.formatMoney(item.price);

    const qtyEl = fragment.querySelector('[data-cart-modal-product-qty]');
    if (qtyEl) qtyEl.textContent = item.quantity;

    const imageEl = fragment.querySelector('[data-cart-modal-product-image]');
    if (imageEl) {
      const imageUrl = item.image || item.featured_image?.url;
      imageEl.innerHTML = imageUrl
        ? `<img src="${imageUrl}" alt="${(item.product_title || '').replace(/"/g, '&quot;')}" loading="lazy" width="90" height="90">`
        : '';
    }

    const countEl = fragment.querySelector('[data-cart-modal-count]');
    if (countEl) {
      const count = totalQuantity ?? item.quantity;
      const noun = count === 1 ? 'item' : 'item(s)';
      const template = this.dataset.countTemplate || 'There are [count] item(s) in your cart.';
      countEl.textContent = template.replace('[count]', count).replace('item(s)', noun);
    }

    const lineTotal = item.line_price ?? item.price * item.quantity;

    const subtotalEl = fragment.querySelector('[data-cart-modal-subtotal]');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(lineTotal);

    const subtotalCompareEl = fragment.querySelector('[data-cart-modal-subtotal-compare]');
    if (subtotalCompareEl) {
      const compareLineTotal = compareAtPrice ? compareAtPrice * item.quantity : null;

      if (compareLineTotal && compareLineTotal > lineTotal) {
        subtotalCompareEl.textContent = this.formatMoney(compareLineTotal);
        subtotalCompareEl.hidden = false;
      } else {
        subtotalCompareEl.textContent = '';
        subtotalCompareEl.hidden = true;
      }
    }

    this.content.innerHTML = '';
    this.content.appendChild(fragment);
  }

  // shop.money_format can use any of Shopify's amount placeholders — this
  // store uses amount_with_comma_separator — and can carry markup, so defer to
  // the theme's helper and strip tags, since the result goes into textContent.
  formatMoney(cents) {
    if (typeof cents !== 'number') return '';

    const format = ((typeof Theme !== 'undefined' && Theme.moneyFormat) || '${{amount}}').replace(/<[^>]*>/g, '');
    const currency = window.Shopify?.currency?.active || 'USD';

    return formatMoneyWithFormat(cents, format, currency);
  }

  open() {
    this.classList.add('is-active');
    document.documentElement.classList.add('amd-cart-modal-open');
    this.dialog?.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.classList.remove('is-active');
    document.documentElement.classList.remove('amd-cart-modal-open');
    this.dialog?.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('pdp-cart-modal')) {
  customElements.define('pdp-cart-modal', PdpCartModal);
}
